import type { LocalStore } from "./storage.js";
import type {
  ConsentDecisions,
  ConsentPolicyContent,
  PublicConsentPolicy,
  StoredConsent,
} from "./types.js";

/**
 * Storage key for the visitor's most recent decision. Keyed (transparently
 * via LocalStore's per-project prefix) so multiple projects on the same
 * origin can't read each other's decisions.
 */
const KEY = "consent";

/**
 * ConsentManager v2 — policy-aware.
 *
 *   * Loads the current decision from localStorage on construction.
 *   * `applyPolicy(policy)` is called once the SDK has fetched the policy
 *     from the server. If the policy's contentHash differs from the stored
 *     decision's policyHash (or no decision is stored), `pendingDecision`
 *     becomes true and the banner host renders.
 *   * `record(decisions, source)` persists locally AND fires the server-side
 *     POST so the controller has GDPR Art. 7(1) proof.
 *   * `allowsCategory(key)` is the single gate used by `Analytics.track()`
 *     and `CaptureResolver.resolveSuper()`.
 *
 * The class itself never touches the DOM — banner UI lives in `banner.ts`.
 * That separation keeps the consent state machine testable.
 */
export class ConsentManager {
  private storage: LocalStore;
  private decisions: ConsentDecisions = {};
  private policy: PublicConsentPolicy | null = null;
  private storedHash: string | null = null;
  private storedVersion: number | null = null;
  private listeners = new Set<(s: ConsentDecisions) => void>();
  private serverPost: ((d: ConsentDecisions, source: ConsentSource) => Promise<void>) | null = null;

  constructor(storage: LocalStore) {
    this.storage = storage;
    const stored = storage.getJson<StoredConsent>(KEY);
    if (stored) {
      this.decisions = stored.decisions ?? {};
      this.storedHash = stored.policyHash ?? null;
      this.storedVersion = stored.policyVersion ?? null;
    }
  }

  setServerSync(fn: (d: ConsentDecisions, source: ConsentSource) => Promise<void>): void {
    this.serverPost = fn;
  }

  /**
   * Returns true if the visitor has not yet decided OR the policy has
   * materially changed since their last decision. In either case the banner
   * must render.
   */
  needsDecision(): boolean {
    if (!this.policy) return false;
    if (this.storedHash == null) return true;
    // contentHash empty means "no published policy yet" — accept the stored
    // decision so we don't spam an empty/unconfigured banner on every load.
    if (this.policy.contentHash === "") return false;
    return this.policy.contentHash !== this.storedHash;
  }

  applyPolicy(policy: PublicConsentPolicy): void {
    this.policy = policy;
    // Re-fill missing category defaults so a new category added since the
    // last decision gets its default state instead of `undefined`.
    for (const cat of policy.content.categories) {
      if (this.decisions[cat.key] == null) {
        this.decisions[cat.key] = cat.required ? true : cat.defaultState;
      }
    }
  }

  getPolicy(): PublicConsentPolicy | null {
    return this.policy;
  }

  get(): ConsentDecisions {
    return { ...this.decisions };
  }

  /**
   * Computes effective category state: required categories are always
   * allowed regardless of stored decision; others use the decision if
   * the visitor has decided, else fall back to the category's default.
   */
  allowsCategory(key: string): boolean {
    const cat = this.policy?.content.categories.find((c) => c.key === key);
    if (!cat) return false;
    if (cat.required) return true;
    if (this.needsDecision()) return false;
    const v = this.decisions[key];
    return typeof v === "boolean" ? v : cat.defaultState;
  }

  /**
   * Backwards-compatible shorthand kept on the class so existing call sites
   * in `Analytics.track()` don't break during the rollout. Treats the
   * legacy "analytics" category as the single gate.
   */
  allowAnalytics(): boolean {
    if (!this.policy) return true; // pre-policy boot: don't block
    return this.allowsCategory("analytics");
  }

  /**
   * Records the visitor's decision. Persists locally first (synchronous)
   * so the next `track()` call sees the new state immediately, then fires
   * the server log asynchronously.
   */
  record(decisions: ConsentDecisions, source: ConsentSource): void {
    if (!this.policy) return;
    // Required categories are forced to `true` — the visitor can't opt out.
    for (const cat of this.policy.content.categories) {
      if (cat.required) decisions[cat.key] = true;
    }
    this.decisions = { ...this.decisions, ...decisions };
    this.storedHash = this.policy.contentHash;
    this.storedVersion = this.policy.version;
    const payload: StoredConsent = {
      decisions: this.decisions,
      policyHash: this.policy.contentHash,
      policyVersion: this.policy.version,
      givenAt: new Date().toISOString(),
      source,
    };
    this.storage.setJson(KEY, payload);
    for (const fn of this.listeners) fn(this.decisions);
    if (this.serverPost) {
      void this.serverPost(this.decisions, source).catch(() => {
        // Server unreachable — local state is still authoritative for the
        // visitor's experience. The next page load will retry the log via
        // a queued post if we add one; for now we just swallow.
      });
    }
  }

  /** Accept every non-required category in the current policy. */
  acceptAll(source: ConsentSource = "banner"): void {
    if (!this.policy) return;
    const next: ConsentDecisions = {};
    for (const cat of this.policy.content.categories) next[cat.key] = true;
    this.record(next, source);
  }

  /** Reject every non-required category. */
  rejectAll(source: ConsentSource = "banner"): void {
    if (!this.policy) return;
    const next: ConsentDecisions = {};
    for (const cat of this.policy.content.categories) next[cat.key] = cat.required;
    this.record(next, source);
  }

  /**
   * Withdraws all consent. Clears stored decision so the banner re-appears
   * on next load; required categories stay enabled (definition).
   */
  withdraw(): void {
    if (!this.policy) return;
    const next: ConsentDecisions = {};
    for (const cat of this.policy.content.categories) next[cat.key] = cat.required;
    this.record(next, "withdraw");
  }

  /**
   * The policy as it should appear in the banner / preferences UI. Includes
   * a dynamically-derived "data we collect" list pulled from the capture
   * rules snapshot — so adding a new property in the dashboard surfaces
   * automatically in the visitor's preferences view without any banner copy
   * edit.
   */
  visibleContent(): RenderableContent | null {
    if (!this.policy) return null;
    const groups = new Map<string, { property: string; sensitive: boolean }[]>();
    for (const r of this.policy.capturedRules) {
      const list = groups.get(r.categoryKey) ?? [];
      list.push({ property: r.property, sensitive: r.isSensitive });
      groups.set(r.categoryKey, list);
    }
    return {
      content: this.policy.content,
      version: this.policy.version,
      categoryRules: this.policy.content.categories.map((c) => ({
        category: c,
        captures: groups.get(c.key) ?? [],
      })),
    };
  }

  onChange(fn: (s: ConsentDecisions) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** For debugging: forces a re-prompt on next page load. */
  reset(): void {
    this.storage.remove(KEY);
    this.decisions = {};
    this.storedHash = null;
    this.storedVersion = null;
  }
}

export type ConsentSource =
  | "banner"
  | "preferences"
  | "api"
  | "withdraw"
  | "implicit_necessary";

export interface RenderableContent {
  content: ConsentPolicyContent;
  version: number;
  categoryRules: {
    category: ConsentPolicyContent["categories"][number];
    captures: { property: string; sensitive: boolean }[];
  }[];
}
